import React, { useState } from 'react';
import { Shield, ShieldCheck } from 'lucide-react';
import { usePrivacyMode } from '../../contexts/PrivacyModeContext';
import PrivacyModeOffDialog from '../PrivacyModeOffDialog';

/**
 * PrivacyModeShield — header entry point for Privacy Mode.
 *
 * OFF state: outline shield. Click → turns Privacy Mode ON instantly.
 * ON state:  filled emerald shield + small emerald dot. Click → opens
 *            the password-gated Off dialog. A nosy neighbour can't
 *            just tap it back off.
 */
const PrivacyModeShield = () => {
  const { privacyMode, turnOn, turnOff } = usePrivacyMode();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleClick = () => {
    if (privacyMode) setDialogOpen(true);
    else turnOn();
  };

  const label = privacyMode
    ? 'Privacy Mode is on — tap to turn off'
    : 'Turn Privacy Mode on';

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        title={label}
        aria-label={label}
        aria-pressed={privacyMode}
        data-testid="privacy-mode-shield"
        data-privacy-mode-state={privacyMode ? 'on' : 'off'}
        className={[
          'relative inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors',
          privacyMode
            ? 'bg-emerald-600 text-white hover:bg-emerald-700'
            : 'bg-white text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 border border-slate-200',
        ].join(' ')}
      >
        {privacyMode
          ? <ShieldCheck className="w-4 h-4" aria-hidden="true" />
          : <Shield className="w-4 h-4" aria-hidden="true" />}
        {privacyMode && (
          <span
            className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-white"
            aria-hidden="true"
          />
        )}
      </button>
      <PrivacyModeOffDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={turnOff}
      />
    </>
  );
};

export default PrivacyModeShield;
